import React, { useState, useRef } from "react";
import Layout from "../components/Layout";
import emailjs from "@emailjs/browser";

function Contact() {
  // Form fields as local state
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [status, setStatus] = useState("");

  // Ref for the form (required by EmailJS)
  const formRef = useRef(null);

  // Checkboxes for primary reasons (only used in the UI)
  const [primaryReasons, setPrimaryReasons] = useState({
    verifyTreatment: false,
    affordableDentist: false,
    alternativeOptions: false,
  });

  // Handle checkbox changes
  const handleReasonChange = (e) => {
    const { name, checked } = e.target;
    setPrimaryReasons((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // On form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validations
    if (!fullName.trim()) {
      setStatus("Please enter your full name.");
      return;
    }
    if (!phone.trim()) {
      setStatus("Please enter your phone number.");
      return;
    }
    if (!termsAccepted) {
      setStatus("You must accept the terms and conditions.");
      return;
    }

    // Collect only the selected reasons
    const selected = [];
    if (primaryReasons.verifyTreatment) {
      selected.push("Verify Treatment");
    }
    if (primaryReasons.affordableDentist) {
      selected.push("Affordable Dentist");
    }
    if (primaryReasons.alternativeOptions) {
      selected.push("Alternative Options");
    }

    if (selected.length === 0) {
      setStatus("Please select at least one primary reason.");
      return;
    }

    // Convert selected reasons into a single string
    const selectedReasonsStr = selected.join(", ");

    // 1) Populate a hidden input with the combined reasons
    //    so EmailJS sees them as a single field "selectedReasons"
    const hiddenReasonsInput = document.getElementById("selectedReasons");
    hiddenReasonsInput.value = selectedReasonsStr;

    // 2) Send the form data via EmailJS
    emailjs
      .sendForm(
        "service_wp56fzn",     // Your EmailJS publicapi
        "template_5kalsdl",    // Your EmailJS TemplateId
        formRef.current,       // Form reference
        "OTsun28Uoa1SZJS2e"    // Your Public Key (User ID)
      )
      .then(() => {
        setStatus("Your message has been sent successfully!");
      })
      .catch(() => {
        setStatus("Oh no! Something went wrong. Please try again.");
      });

    // Reset the form
    setFullName("");
    setPhone("");
    setNotes("");
    setTermsAccepted(false);
    setPrimaryReasons({
      verifyTreatment: false,
      affordableDentist: false,
      alternativeOptions: false,
    });
  };

  return (
    <Layout>
      <div className="max-w-screen-xl mx-auto px-6 lg:px-20 py-16">
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">
          Contact Us
        </h1>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto space-y-4 bg-gray-50 p-6 rounded-md shadow"
        >
          {/* Full Name */}
          <div>
            <label className="block font-medium mb-1" htmlFor="fullName">
              Full Name
            </label>
            <input
              id="fullName"
              name="fullName" // For EmailJS
              type="text"
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block font-medium mb-1" htmlFor="phone">
              Phone Number
            </label>
            <input
              id="phone"
              name="phone" // For EmailJS
              type="tel"
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>

          {/* Primary Reason (Checkboxes) */}
          <div>
            <label className="block font-medium mb-1">
              What is your primary reason for seeking a second opinion?
            </label>
            <div className="flex flex-col gap-2 pl-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="verifyTreatment" // Not used directly by EmailJS
                  checked={primaryReasons.verifyTreatment}
                  onChange={handleReasonChange}
                  className="mr-2"
                />
                Verify if the recommended treatment is necessary
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="affordableDentist" // Not used directly by EmailJS
                  checked={primaryReasons.affordableDentist}
                  onChange={handleReasonChange}
                  className="mr-2"
                />
                Looking for an affordable treatment/dentist
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="alternativeOptions" // Not used directly by EmailJS
                  checked={primaryReasons.alternativeOptions}
                  onChange={handleReasonChange}
                  className="mr-2"
                />
                Exploring alternative treatment options
              </label>
            </div>
          </div>

          {/* Hidden field to store combined reasons */}
          <input
            type="hidden"
            id="selectedReasons"
            name="selectedReasons" // Field used in the EmailJS template
          />

          {/* Notes to Clinic */}
          <div>
            <label className="block font-medium mb-1" htmlFor="notes">
              Notes to Clinic
            </label>
            <textarea
              id="notes"
              name="notes" // For EmailJS
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
              rows="4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Terms & Conditions */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
              className="mr-2"
            />
            <label htmlFor="terms" className="font-medium">
              I agree to the Terms and Conditions
            </label>
          </div>

          {/* Status Message */}
          {status && (
            <p className="text-center text-sm text-red-600 mt-2">
              {status}
            </p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-orange text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition duration-300"
          >
            Submit
          </button>
        </form>
      </div>
    </Layout>
  );
}

export default Contact;
